import React, { useState, useMemo } from 'react';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import ProductListItem from '../components/ProductListItem';
import Pagination from '../components/Pagination';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGrid, FiList, FiSearch, FiCheck, FiSettings, FiInbox, FiArrowUp, FiArrowDown } = FiIcons;

const ITEMS_PER_PAGE = 16;
const ALL_FIELDS = ['ProductID', 'ProductName', 'AggregateQty', 'SitePrice', 'SiteCost', 'UPC', 'ASIN', 'Categories', 'AmazonEnabled', 'eBayEnabled', 'WebEnabled', 'Location'];
const FIELD_LABELS = { ProductID: 'SKU', ProductName: 'Product', AggregateQty: 'Stock', SitePrice: 'Price', SiteCost: 'Cost', UPC: 'UPC', ASIN: 'ASIN', Categories: 'Category', AmazonEnabled: 'Amazon', eBayEnabled: 'eBay', WebEnabled: 'WebStore', Location: 'Location' };

const Dashboard = () => {
  const { products, loading, isConnected } = useProducts();
  const [viewType, setViewType] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('ProductName-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const defaults = ['ProductName', 'AggregateQty', 'SitePrice', 'Location', 'AmazonEnabled', 'WebEnabled'];
    return ALL_FIELDS.reduce((acc, field) => ({ ...acc, [field]: defaults.includes(field) }), {});
  });

  const toggleSort = (field) => {
    setSortOption(prev => {
      const [prevField, prevDir] = prev.split('-');
      return prevField === field ? `${field}-${prevDir === 'asc' ? 'desc' : 'asc'}` : `${field}-asc`;
    });
  };

  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(p => 
        (p.ProductName || '').toLowerCase().includes(lowerQuery) || 
        (p.ProductID || '').toLowerCase().includes(lowerQuery) ||
        (p.UPC || '').toLowerCase().includes(lowerQuery)
      );
    }
    const [field, direction] = sortOption.split('-');
    result.sort((a, b) => {
      let valA = a[field], valB = b[field];
      if (['AggregateQty', 'SitePrice', 'SiteCost'].includes(field)) {
        valA = parseFloat(valA) || 0; valB = parseFloat(valB) || 0;
      } else {
        valA = (valA || '').toString().toLowerCase(); valB = (valB || '').toString().toLowerCase();
      }
      return direction === 'asc' ? (valA < valB ? -1 : 1) : (valA > valB ? -1 : 1);
    });
    return result;
  }, [products, searchQuery, sortOption]);

  const paginatedProducts = filteredAndSortedProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div></div>;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-light text-gray-900 dark:text-white transition-colors">Inventory Catalog</h1>
            {isConnected && (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-medium uppercase tracking-wider border border-emerald-100 dark:border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Live
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{filteredAndSortedProducts.length} items</p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="relative w-full sm:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"><SafeIcon icon={FiSearch} /></div>
            <input 
              type="text" placeholder="Search SKU, Name, or UPC..." 
              className="w-full pl-10 pr-4 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-100 dark:focus:ring-gray-800 transition-all placeholder:text-gray-400"
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <div className="relative">
              <button 
                onClick={() => setShowColumnPanel(!showColumnPanel)}
                className={`p-2 rounded-lg border transition-all flex items-center gap-2 text-sm ${showColumnPanel ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-800'}`}
              >
                <SafeIcon icon={FiSettings} />
              </button>
              
              {showColumnPanel && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow-2xl z-30 overflow-hidden">
                  <div className="p-3 border-b border-gray-50 dark:border-gray-800 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Columns</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto p-1 custom-scrollbar">
                    {ALL_FIELDS.map(field => (
                      <button
                        key={field} onClick={() => setVisibleColumns(prev => ({...prev, [field]: !prev[field]}))}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        <span>{FIELD_LABELS[field]}</span>
                        {visibleColumns[field] && <SafeIcon icon={FiCheck} className="text-emerald-500" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
              <button onClick={() => setViewType('grid')} className={`p-1.5 rounded-md transition-all ${viewType === 'grid' ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'}`}><SafeIcon icon={FiGrid} /></button>
              <button onClick={() => setViewType('list')} className={`p-1.5 rounded-md transition-all ${viewType === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'}`}><SafeIcon icon={FiList} /></button>
            </div>
          </div>
        </div>
      </div>

      {paginatedProducts.length > 0 ? (
        <>
          {viewType === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {paginatedProducts.map(product => <ProductCard key={product.id} product={product} />)}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse min-w-max">
                  <thead className="bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                    <tr>
                      <th className="py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 cursor-pointer" onClick={() => toggleSort('ProductName')}>
                        Product {sortOption.startsWith('ProductName') && <SafeIcon icon={sortOption.endsWith('asc') ? FiArrowUp : FiArrowDown} className="ml-1 inline" />}
                      </th>
                      {ALL_FIELDS.filter(f => f !== 'ProductName' && visibleColumns[f]).map(field => (
                        <th key={field} className="py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest cursor-pointer" onClick={() => toggleSort(field)}>
                          {FIELD_LABELS[field]}
                          {sortOption.startsWith(field) && <SafeIcon icon={sortOption.endsWith('asc') ? FiArrowUp : FiArrowDown} className="ml-1 inline" />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {paginatedProducts.map(product => <ProductListItem key={product.id} product={product} visibleColumns={visibleColumns} allFields={ALL_FIELDS} />)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <Pagination currentPage={currentPage} totalPages={Math.ceil(filteredAndSortedProducts.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
        </>
      ) : (
        <div className="text-center py-24 bg-gray-50/50 dark:bg-gray-900/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800">
          <SafeIcon icon={FiInbox} className="mx-auto text-5xl text-gray-200 dark:text-gray-800 mb-4" />
          <h3 className="text-base font-medium text-gray-900 dark:text-white">No results found</h3>
        </div>
      )}
    </div>
  );
};

export default Dashboard;